import { useCallback } from 'react';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { isEmpty } from 'ramda';
import { useDispatch, useSelector } from 'react-redux';

import { getLabelMessage } from '@gef-ui/localization/messages';

import { ComponentLoader } from 'components';
import PaginationRow from 'components/atoms/GefMui/PaginationRow';
import SortCell from 'components/atoms/GefMui/SortCell';
import TableHasNoResults from 'components/atoms/GefMui/TableHasNoResults';
import { useIgnoreErrorsWhen } from 'components/molecules/ComponentLoader/hooks';
import fetch from 'features/common/fetch';
import policyOverview from 'features/views/policyOverview';
import { SUBSELLER_NOT_FOUND_ERROR_MSG } from 'features/views/policyOverview/constants';
import { useSortColumnsDataLayer } from 'features/views/policyOverview/hooks';
import { getHasPPZSubordinatesOrIsPPZSubordinate } from 'features/views/user/selectors';

import FilterCriteriaChips from './FilterCriteriaChips';
import ResultsTableRow from './ResultsTableRow';

export const ResultsTable = () => {
	const dispatch = useDispatch();

	const sort = useSelector(policyOverview.selectors.getSort);
	const policies = useSelector(policyOverview.selectors.getPolicies);
	const hasRecords = !isEmpty(policies);
	const hasPPZSubordinatesOrIsPPZSubordinate = useSelector(getHasPPZSubordinatesOrIsPPZSubordinate);
	const hasPermissionISP = useSelector(policyOverview.selectors.getPermissionISP);
	const fetchState = useSelector(fetch.selectors.getFetchState(fetch.constants.urls.policyList));
	const { body: lastRequestBody } = useSelector(fetch.selectors.getLastRequest(fetch.constants.urls.policyList));

	const pushToSortColumnDataLayer = useSortColumnsDataLayer();

	const handleSort = useCallback(
		(sortName) => (sort) => {
			dispatch(policyOverview.actions.sortContracts(sort));
			pushToSortColumnDataLayer({ value: sortName })();
		},
		[dispatch, pushToSortColumnDataLayer],
	);

	const handleLoadMore = useCallback(() => {
		dispatch(policyOverview.actions.loadMore());
	}, [dispatch]);

	const handlePaginationClick = useCallback(
		(e, page) => {
			dispatch(policyOverview.actions.loadPage(page - 1));
		},
		[dispatch],
	);

	const ignoreErrorsForSubsellerNotFound = useIgnoreErrorsWhen({
		fetchStorePath: fetch.constants.urls.policyList,
		attributePath: ['metaData', 'parsedContent', 'message'],
		value: SUBSELLER_NOT_FOUND_ERROR_MSG,
	});

	return (
		<Stack spacing={2}>
			{/* show criteria chips on every search, even when when request ends in error (tooManyResults, subsellerNotFound) */}
			{(fetchState.loaded || fetchState.error) && <FilterCriteriaChips />}
			<ComponentLoader
				fetchStorePath={fetch.constants.urls.policyList}
				ignoreErrors={ignoreErrorsForSubsellerNotFound}
			>
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell />
							<TableCell>
								<SortCell
									hasRecords={hasRecords}
									sortBy="policyNo"
									sort={sort}
									onSort={handleSort(getLabelMessage('policyOverview:insContrNoView'))}
								>
									{getLabelMessage('policyOverview:insContrNoView')}
								</SortCell>
								<SortCell
									hasRecords={hasRecords}
									sortBy={['policyHolderSurname', 'policyHolderFirstName']}
									sort={sort}
									onSort={handleSort(getLabelMessage('policyOverview:policyHolderView'))}
								>
									{getLabelMessage('policyOverview:policyHolderView')}
								</SortCell>
							</TableCell>
							<TableCell>
								<div>{getLabelMessage('policyOverview:carRegNo')}</div>
							</TableCell>
							<TableCell>
								<div>{getLabelMessage('policyOverview:municipality')}</div>
								<div>{getLabelMessage('policyOverview:zipCode')}</div>
							</TableCell>
							<TableCell>{getLabelMessage('policyOverview:insuranceView')}</TableCell>
							<TableCell>
								<div>{getLabelMessage('policyOverview:anualPremiumView')}</div>
								<SortCell
									hasRecords={hasRecords}
									sortBy="policyStatusId"
									sort={sort}
									onSort={handleSort(getLabelMessage('policyOverview:policyStatus'))}
								>
									{getLabelMessage('policyOverview:policyStatus')}
								</SortCell>
							</TableCell>
							<TableCell>
								<SortCell
									hasRecords={hasRecords}
									sortBy="policyOpenDate"
									sort={sort}
									onSort={handleSort(getLabelMessage('policyOverview:dealDateView'))}
								>
									{getLabelMessage('policyOverview:dealDateView')}
								</SortCell>
								{getLabelMessage('policyOverview:policyPaymentStatus')}
							</TableCell>
							<TableCell>
								<SortCell
									hasRecords={hasRecords}
									sortBy="policyValueDate"
									sort={sort}
									onSort={handleSort(getLabelMessage('policyOverview:startDateView'))}
								>
									{getLabelMessage('policyOverview:startDateView')}
								</SortCell>
								<SortCell
									hasRecords={hasRecords}
									sortBy="policyFinalDate"
									sort={sort}
									onSort={handleSort(getLabelMessage('policyOverview:endDateView'))}
								>
									{getLabelMessage('policyOverview:endDateView')}
								</SortCell>
							</TableCell>
							{hasPPZSubordinatesOrIsPPZSubordinate && (
								<TableCell>{getLabelMessage('policyOverview:policySubseller')}</TableCell>
							)}
							{lastRequestBody?.searchAcrossSubordinates && <TableCell />}
							{hasPermissionISP && <TableCell />}
						</TableRow>
					</TableHead>
					<TableBody>
						{!hasRecords ? (
							<TableHasNoResults fetchState={fetchState} />
						) : (
							policies.map((policy) => (
								<TableRow key={policy.policyNo}>
									<ResultsTableRow policy={policy} hasPermissionISP={hasPermissionISP} />
								</TableRow>
							))
						)}
					</TableBody>
					<PaginationRow
						colSpan={100}
						fetchStorePath={fetch.constants.urls.policyList}
						onLoadNext={handleLoadMore}
						onPaginationClick={handlePaginationClick}
						hideOnResultsLimit
					/>
				</Table>
			</ComponentLoader>
		</Stack>
	);
};

export default ResultsTable;
